/**
 * 会话行回流 → 该弹哪些通知（任务 18c2，纯模型）。
 *
 * 18b 给的是「单条到达要不要弹」（`shouldNotify`）；本文件管**一批会话行**：
 * 主线程拿到的从来不是单条消息，而是 Worker 帧级合并后的会话行数组
 * （`GlobalState.conversations.rows`，8c）。谁前进了、该弹几条、剩下的怎么记，
 * 都在这里判，接线层（`~/store/notifyDelivery`）只负责调浏览器 API。
 *
 * 三条刻意的口径：
 *
 * 1. **首批只种不弹**（`seed`）。冷启动从 IndexedDB 回流的会话行全是「已经在那儿
 *    的」未读，用户刚打开应用，不该被一串通知糊脸。所以第一批只记 lastMsgId。
 *    之后的全量 replace（connectok 对账，8c）不是 seed——那时离线期间真来了新消息，
 *    该弹。
 * 2. **前进了就记，不管弹没弹**（`advanced` 含被过滤/被限流的行）。否则「免打扰
 *    开着 / 总开关关着 / 超出限流」的消息会在设置改变后被补弹，或在下一次无关
 *    回流时诈尸。记住 = 这条已经「见过」。
 * 3. **每批限流 `NOTIFY_BATCH_LIMIT` 条**。tag 只能折叠同一会话（18b），一批里
 *    20 个会话各来一条就是 20 条系统通知。超出的只记不弹（`throttled` 只用于
 *    测试/排障断言，不进 UI）。窗口内提示条（18c2b）同额度另算：它是另一块屏幕
 *    区域，与系统通知不抢同一个额度。
 *
 * 18c2b 起本文件同时产出两条通道的结果（`contents` 系统通知 / `popups` 窗口内
 * 提示条）。两者**互斥**：系统通知只在非前台弹、提示条只在前台出，由
 * `notifyDecision` 的前台判定保证，所以一行最多进其中一个数组。
 */
import {
  shouldNotify,
  shouldPopupInApp,
  type NotifyContent,
  type NotifyContext,
} from './notifyDecision';
import type { ConversationRecord } from '~/worker/contract';

/** 一批回流最多弹几条系统通知（超出只记不弹）；窗口内提示条同额度另算 */
export const NOTIFY_BATCH_LIMIT = 3;

export interface ArrivalScan {
  /** 本批要弹的系统通知（已按行序＝会话列表序，最新的在前） */
  readonly contents: readonly NotifyContent[];
  /** 本批要出的窗口内提示条（18c2b，桌面前台；与 contents 互斥，见 notifyDecision） */
  readonly popups: readonly NotifyContent[];
  /** 要写回「已见」表的 [会话键, lastMsgId]——含被过滤与被限流的行 */
  readonly advanced: readonly (readonly [string, number])[];
  /** 被限流丢掉的系统通知条数（排障/测试用） */
  readonly throttled: number;
  /** 被限流丢掉的提示条条数（同上） */
  readonly throttledPopups: number;
}

export interface ScanOptions {
  /** true = 首批，只种「已见」不弹任何通知 */
  readonly seed: boolean;
  readonly limit?: number;
}

const EMPTY_SCAN: ArrivalScan = {
  contents: [],
  popups: [],
  advanced: [],
  throttled: 0,
  throttledPopups: 0,
};

export function scanArrivals(
  rows: readonly ConversationRecord[] | null,
  seen: ReadonlyMap<string, number>,
  ctx: NotifyContext,
  opts: ScanOptions,
): ArrivalScan {
  if (rows === null || rows.length === 0) return EMPTY_SCAN;
  const limit = opts.limit ?? NOTIFY_BATCH_LIMIT;
  const advanced: Array<readonly [string, number]> = [];
  const contents: NotifyContent[] = [];
  const popups: NotifyContent[] = [];
  let throttled = 0;
  let throttledPopups = 0;

  for (const row of rows) {
    const seenLastMsgId = seen.get(row.key) ?? 0;
    const { lastMsgId } = row.remote;
    if (lastMsgId <= seenLastMsgId) continue;
    advanced.push([row.key, lastMsgId]);
    if (opts.seed) continue;
    const arrival = { row, seenLastMsgId };
    // 两条通道各判一次：前台判定互补，同一行不可能同时命中（notifyDecision）
    const decision = shouldNotify(arrival, ctx);
    if (decision.notify) {
      if (contents.length >= limit) throttled += 1;
      else contents.push(decision.content);
      continue;
    }
    const popup = shouldPopupInApp(arrival, ctx);
    if (!popup.notify) continue;
    if (popups.length >= limit) throttledPopups += 1;
    else popups.push(popup.content);
  }
  return { contents, popups, advanced, throttled, throttledPopups };
}
